import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateWatchlistItem, UpsertDashboard } from '@esgdash/shared';
import { api, type SeriesQuery } from '../lib/api';

// --- platform meta ---------------------------------------------------------
export function useHealth() {
  return useQuery({ queryKey: ['health'], queryFn: api.health, staleTime: 5 * 60_000 });
}

// --- catalog ---------------------------------------------------------------
export function useCatalog(filters: { category?: string; source?: string; esg?: boolean } = {}) {
  return useQuery({ queryKey: ['catalog', filters], queryFn: () => api.catalog.list(filters), staleTime: 30 * 60_000 });
}

export function useCatalogSearch(query: string) {
  return useQuery({
    queryKey: ['catalog-search', query],
    queryFn: () => api.catalog.search(query),
    enabled: query.trim().length >= 1,
    staleTime: 5 * 60_000,
  });
}

// --- series ----------------------------------------------------------------
export function useSeries(query: SeriesQuery | null) {
  return useQuery({
    queryKey: ['series', query],
    queryFn: () => api.data.series(query as SeriesQuery),
    enabled: !!query,
    staleTime: 10 * 60_000,
  });
}

// --- watchlist -------------------------------------------------------------
export function useWatchlist(enabled: boolean) {
  return useQuery({ queryKey: ['watchlist'], queryFn: api.watchlist.list, enabled });
}

export function useAddWatchlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (item: CreateWatchlistItem) => api.watchlist.add(item),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['watchlist'] });
      qc.invalidateQueries({ queryKey: ['usage'] });
    },
  });
}

export function useRemoveWatchlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.watchlist.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['watchlist'] });
      qc.invalidateQueries({ queryKey: ['usage'] });
    },
  });
}

// --- dashboards ------------------------------------------------------------
export function useDashboards(enabled: boolean) {
  return useQuery({ queryKey: ['dashboards'], queryFn: api.dashboards.list, enabled });
}

export function useDashboard(id: string | undefined) {
  return useQuery({
    queryKey: ['dashboard', id],
    queryFn: () => api.dashboards.get(id as string),
    enabled: !!id,
  });
}

export function useUpdateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpsertDashboard }) => api.dashboards.update(id, input),
    onSuccess: (dash) => {
      qc.invalidateQueries({ queryKey: ['dashboards'] });
      qc.setQueryData(['dashboard', dash.id], dash);
    },
  });
}

export function useCreateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertDashboard) => api.dashboards.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboards'] });
      qc.invalidateQueries({ queryKey: ['usage'] });
    },
  });
}

export function useDeleteDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.dashboards.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboards'] });
      qc.invalidateQueries({ queryKey: ['usage'] });
    },
  });
}

// --- account ---------------------------------------------------------------
export function useUsage(enabled: boolean) {
  return useQuery({ queryKey: ['usage'], queryFn: api.usage, enabled });
}

export function useApiKeys(enabled: boolean) {
  return useQuery({ queryKey: ['api-keys'], queryFn: api.keys.list, enabled });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.keys.create(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      qc.invalidateQueries({ queryKey: ['usage'] });
    },
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.keys.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      qc.invalidateQueries({ queryKey: ['usage'] });
    },
  });
}
